import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { ChevronLeft, Check, X } from 'feather-icons-react';
import { useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { expandConstants, getFilteredDimensions, getFilteredMetrics, omitProps } from 'utils';

import { GlobalFilter } from 'components/GlobalFilter';
import {
  Button,
  Checkbox,
  DateRangePicker,
  Field,
  FieldLabel,
  IconButton,
  Indicator,
  Input,
  RadioButton,
  RadioGroup,
} from 'components/common';
import { useDefaultDateRangePresets } from 'components/common/dateRangePicker/dateRangePresets';
import { EmailInput } from 'components/common/emailInput';
import { Select, Option } from 'components/common/select';
import SortableList, { DragHandle } from 'components/common/sortableList';

import { useConstantsQuery } from 'modules/apiData/dataApiSlice';
import NotificationService from 'modules/notifications/notificationService';
import { useOrganization } from 'modules/organization/useOrganization';
import { PerformanceReport, ReportStatusResponse } from 'modules/report/reportApiTypes';
import { useReport } from 'modules/report/useReport';

import { Dimension, Metric } from '../../modules/misc/miscApiTypes';
import { DSP_ADFORM, DSP_AMAZON, DSP_CRITEO, DSP_VISTAR, DSP_XANDR } from '../../utils/constants';
import {
  HOURS,
  performanceReportSchema,
  PerformanceFormType,
  CreateReportProps,
  toEnum,
  OVERALL,
  NAME_MAX_LENGTH,
  CUSTOM,
  STATUS_TYPES,
  DEFAULT_DAILY_CADENCE,
  DEFAULT_TIME_SLICE,
  CheckboxField,
  isSchedulingDisabledForCustomRange,
} from './ReportsTypes';
import { usePolling } from './usePolling';

const dateFormat = 'YYYY-MM-DDTHH:mm:ss.SSS';

export const CreatePerformanceReport = ({
  setIsCreateFormOpen,
  selectedReport,
  refetch,
  setReportType,
  reportType,
}: CreateReportProps) => {
  const { currentAdvertiserId, dsp } = useOrganization();
  const { savePerformanceReport, isLoading, downloadReport, getReportStatus } = useReport();
  const { data: constants } = useConstantsQuery(null);
  const { presets: defaultDateRangePresets } = useDefaultDateRangePresets();
  const newReportRef = useRef<PerformanceReport | null>(null);

  const dimensions = constants ? getFilteredDimensions<Dimension>(constants, dsp) : {};
  const metrics = constants ? getFilteredMetrics<Metric>(constants, dsp) : {};

  const cadence = expandConstants((constants && omitProps(constants.Cadence, ['YEARLY'])) || {});
  const attributionSettings = (constants && constants.AttributionSettings) || {};
  const methodologyOptions = expandConstants((constants && constants.MethodologyOptions) || {});
  const conversionWindow = toEnum((constants && constants.ConversionWindow) || []);
  const intervals = expandConstants((constants && constants.Intervals) || {});
  const formats = expandConstants((constants && constants.ReportFormat) || {});
  const weeklyCadence = expandConstants((constants && constants.CadenceWeekly) || {});
  const monthlyCadence = expandConstants((constants && constants.CadenceMonthly) || {});
  const dailyCadence = expandConstants(HOURS);
  const reportTypes = expandConstants((constants && constants.ReportType) || {});
  const timeSlices = (constants && constants.TimeSlice) || {};

  const { startDate, endDate, cadenceType, cadenceValue, timeSlice } = selectedReport || {};
  const existingDateRange =
    startDate && endDate
      ? { startDate: dayjs(startDate), endDate: dayjs(endDate), period: timeSlice }
      : null;

  const defaultTimeSlice = timeSlices[DEFAULT_TIME_SLICE];
  const defaultPreset = defaultDateRangePresets.find((p) => p.label === defaultTimeSlice);
  const defaultDateRange = {
    startDate: defaultPreset?.value?.[0],
    endDate: defaultPreset?.value?.[1],
    period: DEFAULT_TIME_SLICE,
  };
  const getCadenceValue = (type: { value: string }) =>
    cadenceType === type.value ? cadenceValue : null;
  const getExistingDimensions = (dimensions: string[]) =>
    dimensions?.length ? dimensions : [OVERALL];

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PerformanceFormType>({
    resolver: yupResolver(performanceReportSchema),
    mode: 'onSubmit',
    values: {
      reportType: selectedReport?.reportType || reportType,
      name: selectedReport?.name || '',
      dateRange: existingDateRange || defaultDateRange,
      showUniques: selectedReport?.showUniques || attributionSettings.TOTAL.value,
      methodology: selectedReport?.methodology || methodologyOptions.LAST_TOUCH.value,
      conversionWindow: conversionWindow[selectedReport?.conversionWindow || 30],
      conversionSource: selectedReport?.conversionSource || 'TOTAL',
      interval: selectedReport?.interval || intervals.CUMULATIVE.value,
      dimensions: selectedReport ? getExistingDimensions(selectedReport?.dimensions) : [],
      dimensionFilters: selectedReport?.dimensionFilters || [],
      metrics: selectedReport?.metrics || [],
      reportFormat: selectedReport?.reportFormat || formats.CSV.value,
      isScheduled: selectedReport?.isScheduled || false,
      emails: selectedReport?.emails || [],
      cadenceType: cadenceType || cadence.DAILY.value,
      dailyCadence:
        selectedReport?.cadenceHour?.toString() || dailyCadence[DEFAULT_DAILY_CADENCE].value,
      weeklyCadence: getCadenceValue(cadence.WEEKLY) || weeklyCadence.SUNDAY.value,
      monthlyCadence: getCadenceValue(cadence.MONTHLY) || monthlyCadence.FIRST_OF_MONTH.value,
    },
  });
  const headerText = selectedReport ? 'Edit report' : 'Create a report';
  const isScheduleEnabled = watch('isScheduled');
  const isCustomDateRange = watch('dateRange')?.period === CUSTOM;
  const dateRangeEndDate = watch('dateRange')?.endDate;
  const isSchedulingDisabled = isSchedulingDisabledForCustomRange(
    isCustomDateRange,
    dateRangeEndDate,
  );

  useEffect(() => {
    if (isSchedulingDisabled) setValue('isScheduled', false);
  }, [isSchedulingDisabled, setValue]);

  useEffect(() => {
    if (
      !(dsp === DSP_XANDR || dsp === DSP_ADFORM) &&
      watch('reportType') === reportTypes.ALL_TOUCH.value
    ) {
      let newReportType;

      switch (dsp) {
        case DSP_VISTAR:
          newReportType = reportTypes.VISTAR.value;
          break;
        case DSP_CRITEO:
          newReportType = reportTypes.CRITEO.value;
          break;
        case DSP_AMAZON:
          newReportType = reportTypes.AMAZON.value;
          break;
        default:
          newReportType = reportTypes.CRITEO.value;
      }
      setValue('reportType', newReportType);
      setReportType(newReportType);
    }
  }, [dsp, reportTypes, setValue, watch, setReportType]);

  const setEmails = useCallback(
    (value: string[]) => setValue('emails', value, { shouldValidate: true }),
    [setValue],
  );

  const hasOverall = (arr: string[]) => arr.some((i) => i === OVERALL);
  const skipIfOverall = (arr: string[]) => (hasOverall(arr) ? [] : arr);

  const handleFormSubmit = handleSubmit(async (data) => {
    const { startDate, endDate, period: timeSlice } = data.dateRange;

    const dimensions = skipIfOverall(data.dimensions);
    const basePayload = omitProps(data, [
      'reportType',
      'dateRange',
      'weeklyCadence',
      'monthlyCadence',
      'dimensions',
      'dailyCadence',
    ]);

    const weeklyCadence =
      data.cadenceType === cadence.WEEKLY.value ? { weeklyCadence: data.weeklyCadence } : {};
    const monthlyCadence =
      data.cadenceType === cadence.MONTHLY.value ? { monthlyCadence: data.monthlyCadence } : {};
    const reportId = selectedReport ? { reportId: selectedReport.id } : {};

    const payload = {
      ...basePayload,
      advertiserId: currentAdvertiserId,
      startDate: startDate ? startDate?.format(dateFormat) : '',
      endDate: endDate ? endDate?.format(dateFormat) : '',
      timeSlice,
      dimensions,
      ...weeklyCadence,
      ...monthlyCadence,
      dailyCadence: parseInt(data.dailyCadence),
      ...reportId,
    };

    newReportRef.current = await savePerformanceReport(payload);
    refetch();
  });

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    await handleFormSubmit();

    if (newReportRef?.current?.id) setIsCreateFormOpen(false);
  };

  const stopCondition = ({ status }: ReportStatusResponse) =>
    status === STATUS_TYPES.COMPLETED || status === STATUS_TYPES.EMPTY;

  const { isPolling, startPolling, stopPolling } = usePolling(5000, stopCondition, 6);

  const handleStatusResponse = async (
    reportStatus: ReportStatusResponse | undefined,
    onCompleted: () => Promise<void>,
  ) => {
    if (reportStatus) {
      const { status } = reportStatus;

      if (status === STATUS_TYPES.COMPLETED) {
        await onCompleted();
        setIsCreateFormOpen(false);
      }
      if (status === STATUS_TYPES.EMPTY) {
        NotificationService.warn(
          'We’re unable to generate the report at this time as there’s no data available.',
        );
        setIsCreateFormOpen(false);
      }
      if (status === STATUS_TYPES.IN_PROGRESS) {
        NotificationService.warn(
          'Report generation is taking a bit longer than expected. You can download it later from the Reports table.',
        );
      }
    }
  };

  const handleDownloadClick = async () => {
    try {
      await handleFormSubmit();

      const { id, name } = newReportRef.current as PerformanceReport;
      const reportStatus = await startPolling(() => getReportStatus(id));
      const onCompleted = () => downloadReport(id, name);

      handleStatusResponse(reportStatus, onCompleted);
    } catch (err) {
      NotificationService.error('An error occured while downloading a report');
    } finally {
      newReportRef.current = null;
    }
  };

  const handleBack = () => {
    stopPolling();
    setIsCreateFormOpen(false);
  };

  const handleCheckboxChange = (option: string, isChecked: boolean, field: CheckboxField) =>
    field.onChange(isChecked ? [...field.value, option] : field.value.filter((m) => m !== option));

  const handleAll = (allOptions: { value: string }[], checked: boolean, field: CheckboxField) =>
    field.onChange(checked ? allOptions.map(({ value }) => value) : []);

  const allMetricsValues = Object.values(metrics);
  const allDimensionsValues = Object.values(dimensions);

  return (
    <div className="w-full bg-white rounded-xl border border-primary-gray-100 flex-col justify-start items-start flex ctv-create-report">
      <div className="h-20 w-full border-b border-primary-gray-100 flex-col justify-start items-start inline-flex">
        <div className="w-256 px-8 py-5 justify-between items-start inline-flex">
          <div className="justify-start items-center gap-1 flex">
            <IconButton Icon={ChevronLeft} variant="tertiary" onClick={handleBack} />
            <div className="text-secondary-black text-heading-2xl font-bold leading-loose">
              {headerText}
            </div>
          </div>
          <div className="justify-start items-center gap-2 flex">
            <Button
              variant="outline"
              htmlType="submit"
              form="reportForm"
              isLoading={isLoading || isPolling}
            >
              Save
            </Button>
            <Button
              variant="primary"
              disabled={isScheduleEnabled}
              onClick={handleDownloadClick}
              isLoading={isLoading || isPolling}
            >
              Download
            </Button>
          </div>
        </div>
      </div>
      <form
        id="reportForm"
        onSubmit={handleSave}
        className="w-256 p-8 flex-col justify-start items-start gap-8 inline-flex"
      >
        <div>
          <div className="mb-1.5 ">
            <FieldLabel htmlFor="reportType">Report type</FieldLabel>
          </div>
          <RadioGroup
            value={reportType}
            onChange={(option) => {
              setReportType(option.target.value);
            }}
          >
            <div className="self-stretch justify-start items-start gap-3 inline-flex">
              <RadioButton
                cards
                value={(() => {
                  switch (dsp) {
                    case DSP_CRITEO:
                      return reportTypes.CRITEO.value;
                    case DSP_VISTAR:
                      return reportTypes.VISTAR.value;
                    case DSP_ADFORM:
                      return reportTypes.ADFORM_PERFORMANCE.value;
                    case DSP_AMAZON:
                      return reportTypes.AMAZON.value;
                    default:
                      return reportTypes.PERFORMANCE.value;
                  }
                })()}
                className="w-[472px]"
              >
                <div className="justify-center items-center gap-1.5 inline-flex">
                  <div className="card-header">Performance report</div>
                  <Check className="text-primary-electric-500 w-5 h-5" />
                </div>
                <div className="card-text">
                  A breakdown of selected dimensions so that you can effectively monitor which
                  aspects of your campaign are performing.
                </div>
              </RadioButton>
              {dsp === DSP_XANDR || dsp === DSP_ADFORM ? (
                <RadioButton
                  cards
                  value={
                    dsp === DSP_ADFORM
                      ? reportTypes.ADFORM_ALL_TOUCH.value
                      : reportTypes.ALL_TOUCH.value
                  }
                  disabled={!!selectedReport}
                  className="w-[472px]"
                >
                  <div className="justify-center items-center gap-1.5 inline-flex">
                    <div className="card-header">All touch report</div>
                  </div>
                  <div className=" card-text">All touch report</div>
                </RadioButton>
              ) : (
                <div className="invisible w-[472px]" />
              )}
            </div>
          </RadioGroup>
        </div>

        <div className="w-96 flex-col justify-start items-start gap-8 inline-flex">
          <Field
            label={'Name of report'}
            render={({ field, fieldState: { invalid } }) => (
              <Input
                {...field}
                invalid={invalid}
                placeholder="Enter report name..."
                maxLength={NAME_MAX_LENGTH}
              />
            )}
            control={control}
            name={'name'}
            error={errors.name?.message}
            secondaryLabel={
              <div className="text-base-xs leading-tight font-medium flex items-center text-primary-gray-500">
                {`${watch('name')?.length}/${NAME_MAX_LENGTH}`}
              </div>
            }
          />

          <Field
            label={'Date range'}
            render={({ field }) => {
              const { value, ...rest } = field;

              return (
                <DateRangePicker
                  className="w-full"
                  value={[value.startDate, value.endDate]}
                  timeSlice={!isCustomDateRange ? watch('dateRange')?.period : ''}
                  presetOptions={{ withCampaignToDatePreset: true }}
                  allowFutureDates={true}
                  {...rest}
                />
              );
            }}
            control={control}
            name={'dateRange'}
            error={errors.dateRange?.message}
          />

          {dsp === DSP_XANDR || dsp === DSP_ADFORM ? (
            <>
              <Field
                label={'Attribution settings'}
                render={({ field }) => (
                  <RadioGroup {...field} className="w-full">
                    <div className="self-stretch flex">
                      {Object.values(attributionSettings).map(({ value, label }, i) => (
                        <RadioButton key={`attributionSettings-radio-${i}`} value={value}>
                          <div className="justify-center items-center gap-1.5 inline-flex">
                            {label}
                            <Check className="text-primary-electric-500 w-5 h-5 check-icon" />
                          </div>
                        </RadioButton>
                      ))}
                    </div>
                  </RadioGroup>
                )}
                control={control}
                name={'showUniques'}
                error={errors.showUniques?.message}
              />
              <Field
                label={'Methodology'}
                render={({ field }) => (
                  <Select className="w-full" {...field}>
                    {Object.values(methodologyOptions).map(({ value, label }, i) => (
                      <Option key={`methodology-dropdown-${i}`} value={value}>
                        {label}
                      </Option>
                    ))}
                  </Select>
                )}
                control={control}
                name={'methodology'}
                error={errors.methodology?.message}
              />
              <Field
                label={'Conversion window (days)'}
                render={({ field }) => (
                  <Select className="w-full" {...field}>
                    {Object.values(conversionWindow).map((value, i) => (
                      <Option key={`methodology-dropdown-${i}`} value={value}>
                        {value}
                      </Option>
                    ))}
                  </Select>
                )}
                control={control}
                name={'conversionWindow'}
                error={errors.conversionWindow?.message}
              />
            </>
          ) : null}

          <Field
            label={'Interval'}
            render={({ field }) => (
              <Select className="w-full" {...field}>
                {Object.values(intervals).map(({ value, label }, i) => (
                  <Option key={`intervals-dropdown-${i}`} value={value}>
                    {label}
                  </Option>
                ))}
              </Select>
            )}
            control={control}
            name={'interval'}
            error={errors.interval?.message}
          />

          {dsp === DSP_ADFORM && (
            <Field
              label={'Conversion Source'}
              render={({ field }) => (
                <Select className="w-full" {...field}>
                  <Option value="TOTAL">Total</Option>
                  <Option value="PV">Post View</Option>
                  <Option value="PC">Post Click</Option>
                </Select>
              )}
              control={control}
              name={'conversionSource'}
              error={errors.conversionSource?.message}
            />
          )}
        </div>
        <div className="w-full flex-col justify-start items-start gap-8 inline-flex">
          <Field
            label={'Report builder'}
            render={({ field }) => (
              <GlobalFilter
                dimensions={field.value || []}
                onChange={field.onChange}
                showDimensions={true}
                maxFilterCount={1}
              />
            )}
            control={control}
            name={'dimensionFilters'}
            error={errors.dimensionFilters?.message}
          />

          <Field
            render={({ field }) => (
              <div className="self-stretch h-112 w-full bg-white rounded-lg border border-primary-gray-100 flex-col justify-start items-start flex overflow-hidden">
                <div className="self-stretch h-16 px-6 border-b border-primary-gray-100 justify-start items-center inline-flex ">
                  <div className="justify-start items-center gap-1.5 flex">
                    <div className="w-8 h-8 flex-col justify-center items-center inline-flex">
                      <div className="rounded border border-primary-electric-500 justify-start items-start inline-flex overflow-hidden">
                        <div className="w-2.5 h-6 bg-primary-electric-100 border-r border-primary-electric-500" />
                        <div className="w-5 h-6" />
                      </div>
                    </div>
                    <div className="text-secondary-black text-base-sm font-semibold leading-snug">
                      Select dimensions
                    </div>
                  </div>
                </div>
                <div className="self-stretch grow shrink basis-0 justify-start items-start inline-flex overflow-hidden">
                  <div className="px-6 py-2 h-full w-1/2 overflow-y-scroll bg-primary-gray-25 border-r border-primary-gray-100">
                    <Checkbox
                      className="mb-2"
                      checked={hasOverall(field.value)}
                      onChange={(e) => handleCheckboxChange('OVERALL', e.target.checked, field)}
                    >
                      <span className="font-normal leading-tight">Overall</span>
                    </Checkbox>
                    {allDimensionsValues.map(({ value, label: { shownName } }, index) => (
                      <Checkbox
                        key={index}
                        className="mb-2"
                        disabled={hasOverall(field.value)}
                        checked={field.value?.includes(value)}
                        onChange={(e) => handleCheckboxChange(value, e.target.checked, field)}
                      >
                        <span className="font-normal leading-tight">{shownName}</span>
                      </Checkbox>
                    ))}
                  </div>
                  <div className="px-6 py-4 w-1/2 overflow-y-scroll h-full">
                    <div className="text-secondary-black text-base-md font-medium  leading-snug mb-2">
                      Active dimensions
                    </div>
                    <div className="w-full">
                      <SortableList
                        items={skipIfOverall(field.value).map((value) => dimensions[value])}
                        setItems={field.onChange}
                        renderItem={(item) => (
                          <div className="h-10 w-full px-2 bg-primary-gray-50 rounded-lg justify-between items-center inline-flex">
                            <div className="justify-start items-center gap-1.5 flex">
                              <DragHandle />
                              <div className="text-secondary-black text-base-sm font-normal ">
                                {item.label?.shownName}
                              </div>
                            </div>
                            <X
                              className="w-5 h-5 text-secondary-black cursor-pointer hover:text-primary-gray-500"
                              onClick={() => {
                                field.onChange(field.value.filter((d: string) => d !== item.value));
                              }}
                            />
                          </div>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            control={control}
            name={'dimensions'}
            error={errors.dimensions?.message}
          />

          <Field
            render={({ field }) => (
              <div className="self-stretch h-112 w-full bg-white rounded-lg border border-primary-gray-100 flex-col justify-start items-start flex overflow-hidden">
                <div className="self-stretch h-16 px-6 border-b border-primary-gray-100 justify-start items-center inline-flex ">
                  <div className="justify-start items-center gap-1.5 flex">
                    <div className="w-8 h-8 flex-col justify-center items-center inline-flex">
                      <div className="rounded border border-primary-electric-500 justify-start items-start inline-flex overflow-hidden">
                        <div className="w-2.5 h-6 border-r border-primary-electric-500" />
                        <div className="w-5 h-6 bg-primary-electric-100" />
                      </div>
                    </div>
                    <div className="text-secondary-black text-base-sm font-semibold leading-snug">
                      Select metrics
                    </div>
                  </div>
                </div>
                <div className="self-stretch grow shrink basis-0 justify-start items-start inline-flex overflow-hidden">
                  <div className="px-6 py-2 h-full w-1/2 overflow-y-scroll bg-primary-gray-25 border-r border-primary-gray-100">
                    <Checkbox
                      className="mb-2"
                      checked={field.value.length === allMetricsValues.length}
                      onChange={(e) => handleAll(allMetricsValues, e.target.checked, field)}
                    >
                      <span className="font-normal leading-tight">All metrics</span>
                    </Checkbox>
                    {allMetricsValues.map(({ value, label: { shownName } }, index) => (
                      <Checkbox
                        key={index}
                        className="mb-2"
                        checked={field.value?.includes(value)}
                        onChange={(e) => handleCheckboxChange(value, e.target.checked, field)}
                      >
                        <span className="font-normal leading-tight">{shownName}</span>
                      </Checkbox>
                    ))}
                  </div>
                  <div className="px-6 py-4 w-1/2 overflow-y-scroll h-full">
                    <div className="text-secondary-black text-base-md font-medium  leading-snug mb-2">
                      Active metrics
                    </div>
                    <div className="w-full">
                      <SortableList
                        items={skipIfOverall(field.value).map((value) => metrics[value])}
                        setItems={field.onChange}
                        renderItem={(item) => (
                          <div className="h-10 w-full px-2 bg-primary-gray-50 rounded-lg justify-between items-center inline-flex">
                            <div className="justify-start items-center gap-1.5 flex">
                              <DragHandle />
                              <div className="text-secondary-black text-base-sm font-normal ">
                                {item.label?.shownName}
                              </div>
                            </div>
                            <X
                              className="w-5 h-5 text-secondary-black cursor-pointer hover:text-primary-gray-500"
                              onClick={() => {
                                field.onChange(field.value.filter((d: string) => d !== item.value));
                              }}
                            />
                          </div>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            control={control}
            name={'metrics'}
            error={errors.metrics?.message}
          />
        </div>
        <div className="w-96 flex-col justify-start items-start gap-8 inline-flex">
          <Field
            label={'Format'}
            render={({ field }) => (
              <RadioGroup {...field} className="w-full">
                <div className="self-stretch flex">
                  {Object.values(formats)
                    .reverse()
                    .map(({ value, label }, i) => (
                      <RadioButton key={`formats-radio-${i}`} value={value}>
                        <div className="justify-center items-center gap-1.5 inline-flex">
                          {label}
                          <Check className="text-primary-electric-500 w-5 h-5 check-icon" />
                        </div>
                      </RadioButton>
                    ))}
                </div>
              </RadioGroup>
            )}
            control={control}
            name={'reportFormat'}
            error={errors.reportFormat?.message}
          />

          <Field
            disabled={isSchedulingDisabled}
            render={({ field }) => (
              <Checkbox {...field} checked={isSchedulingDisabled ? false : field.value}>
                <span className="text-base-sm font-medium leading-tight">
                  Scheduled email report
                </span>
              </Checkbox>
            )}
            control={control}
            name={'isScheduled'}
            error={errors.isScheduled?.message}
          />

          {isScheduleEnabled ? (
            <EmailInput
              setEmails={setEmails}
              label="Recipient email"
              placeholder="Enter..."
              emails={watch('emails')}
              fieldError={errors.emails?.message}
            />
          ) : null}
        </div>
        <div className="w-168 flex-col justify-start items-start gap-8 inline-flex">
          {isScheduleEnabled ? (
            <Field
              label={'Cadence'}
              render={({ field }) => (
                <RadioGroup {...field} className="w-full">
                  <div className="self-stretch flex">
                    {Object.values(cadence).map(({ value, label }, i) => (
                      <RadioButton key={`cadence-radio-${i}`} value={value}>
                        <div className="justify-center items-center gap-1.5 inline-flex">
                          {label}
                          <Check className="text-primary-electric-500 w-5 h-5 check-icon" />
                        </div>
                      </RadioButton>
                    ))}
                  </div>
                </RadioGroup>
              )}
              control={control}
              name={'cadenceType'}
              error={errors.cadenceType?.message}
            />
          ) : null}

          {isScheduleEnabled ? (
            <div className="h-10 w-full justify-start items-start gap-2 inline-flex">
              {watch('cadenceType') === 'WEEKLY' ? (
                <Field
                  render={({ field }) => (
                    <Select className="w-full" {...field}>
                      {Object.values(weeklyCadence).map(({ value, label }, i) => (
                        <Option key={`weeks-dropdown-${i}`} value={value}>
                          {label}
                        </Option>
                      ))}
                    </Select>
                  )}
                  control={control}
                  name={'weeklyCadence'}
                  error={errors.weeklyCadence?.message}
                />
              ) : null}

              {watch('cadenceType') === 'MONTHLY' ? (
                <Field
                  render={({ field }) => (
                    <Select className="w-full" {...field}>
                      {Object.values(monthlyCadence).map(({ value, label }, i) => (
                        <Option key={`months-dropdown-${i}`} value={value}>
                          {label}
                        </Option>
                      ))}
                    </Select>
                  )}
                  control={control}
                  name={'monthlyCadence'}
                  error={errors.monthlyCadence?.message}
                />
              ) : null}

              <Field
                render={({ field }) => (
                  <Select className="w-full max-w-88" {...field}>
                    {Object.values(dailyCadence).map(({ value, label }, i) => (
                      <Option key={`hours-dropdown-${i}`} value={value}>
                        {label}
                      </Option>
                    ))}
                  </Select>
                )}
                control={control}
                name={'dailyCadence'}
                error={errors.dailyCadence?.message}
              />
            </div>
          ) : null}

          {isSchedulingDisabled ? (
            <Indicator variant="warning" size="lg">
              Scheduling is not available when selecting a custom date range with an end date in the
              past. Please select a future end date to schedule email reports.
            </Indicator>
          ) : null}
        </div>
      </form>
    </div>
  );
};
